import type { CellProperties, RendererFunction } from '../../common/handsontable.types';

/**
 * Shared helper to render a cell with dropdown icon
 * Used by both dropdown and autocomplete renderers
 * @param td - The table cell element
 * @param value - The cell value
 * @param cellProperties - Cell properties from cells function (contains className)
 */
export const renderCellWithIcon = (
  td: HTMLElement,
  value: unknown,
  cellProperties?: CellProperties
): HTMLElement => {
  // IMPORTANT: Save className from cellProperties (set by cells function) BEFORE clearing innerHTML
  // cellProperties.className contains update-modified-cell or update-unchanged-cell
  const cellsClassName = cellProperties?.className || '';
  const existingClassName = td.className || '';

  // Clear existing content
  td.innerHTML = '';

  // Create container for text and icon
  const container = document.createElement('div');
  container.style.display = 'flex';
  container.style.alignItems = 'flex-start';
  container.style.justifyContent = 'space-between';
  container.style.width = '100%';
  container.style.padding = '4px 8px';
  container.style.position = 'relative';
  container.style.wordWrap = 'break-word';
  container.style.overflowWrap = 'break-word';

  // Create text element
  const textSpan = document.createElement('span');
  textSpan.textContent = value != null && value !== '' ? String(value) : '';
  textSpan.style.flex = '1';
  textSpan.style.wordWrap = 'break-word';
  textSpan.style.whiteSpace = 'normal';
  textSpan.style.overflowWrap = 'break-word';

  // Create dropdown icon
  const iconSpan = document.createElement('span');
  iconSpan.innerHTML = '▼';
  iconSpan.style.color = '#666';
  iconSpan.style.fontSize = '10px';
  iconSpan.style.marginLeft = '4px';
  iconSpan.style.marginRight = '8px';
  iconSpan.style.flexShrink = '0';
  iconSpan.style.userSelect = 'none';
  iconSpan.style.cursor = 'pointer';
  iconSpan.style.alignSelf = 'flex-start';
  iconSpan.style.marginTop = '2px';
  iconSpan.className = 'dropdown-icon';

  // Add click handler to open editor
  iconSpan.style.pointerEvents = 'auto';
  iconSpan.addEventListener('click', (e) => {
    e.stopPropagation();
    e.preventDefault();
    // Trigger cell editor
    td.click();
    td.dispatchEvent(new MouseEvent('dblclick', { bubbles: true, cancelable: true }));
  });

  container.appendChild(textSpan);
  container.appendChild(iconSpan);
  td.appendChild(container);

  // IMPORTANT: Preserve className from cells function
  // Combine className from cellProperties (from cells function) with existing className
  // Filter out Handsontable-specific classes that will be re-added
  const classSet = new Set<string>();

  // Add className from cellProperties (from cells function) - this is the most important
  if (cellsClassName) {
    const cellsClasses = Array.isArray(cellsClassName)
      ? cellsClassName
      : String(cellsClassName).split(' ').filter(Boolean);
    cellsClasses.forEach((cls) => classSet.add(String(cls).trim()));
  }

  // Add existing className (may contain current-row-highlight, etc.)
  existingClassName.split(' ').forEach((cls) => {
    const trimmed = cls.trim();
    if (trimmed && !trimmed.startsWith('ht')) {
      classSet.add(trimmed);
    }
  });

  // Set className - Handsontable will add back its own classes (ht*, current, etc.)
  td.className = Array.from(classSet).join(' ');
  delete td.dataset.value;

  return td;
};

/**
 * Custom renderer for dropdown columns to show dropdown icon
 * Used in Handsontable to display dropdown cells with a visual indicator
 * @param cellProperties - Cell properties from cells function (contains className)
 */
export const dropdownRenderer: RendererFunction = (
  _instance,
  td,
  _row,
  _col,
  _prop,
  value,
  cellProperties
) => {
  return renderCellWithIcon(td, value, cellProperties);
};
